import { type ReactNode } from 'react';
import { PiCopyLight, PiCheckBold, PiDotsThreeVertical } from 'react-icons/pi';
import Button from '../../../atoms/buttons/button';
import Label from '../../../atoms/inputs/label';
import {
  type ComponentVariant,
  type ComponentSize,
} from 'src/modules/shared/types/component.type';
import { useCopyToClipboard } from 'src/modules/shared/hooks/copy-to-clipboard';
import { getInlineIconSize } from 'src/modules/shared/components/atoms/buttons/button/button.styles';
import { PiArrowSquareOut } from 'react-icons/pi';
import Tooltip from '../../../atoms/tooltips/tooltip';
import Dropdown, {
  type DropdownItem,
} from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import {
  isProbablyUrl,
  ensureUrlWithProtocol,
} from 'src/modules/shared/utils/validation/url.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { stackedFieldValueClassName } from 'src/modules/shared/styles/stacked-field.styles';

interface InlineInputProps<T> {
  value: T;
  onChange: (value: T) => void;
  disabled: boolean;
  loading?: boolean;
}

export interface InlineInputWrapperProps<T = string> {
  label: string;
  value: T;
  onSave: (value: T) => Promise<void>;
  loading?: boolean;
  size?: ComponentSize;
  variant?: ComponentVariant;
  disabled?: boolean;
  disableCopy?: boolean;
  hideLabel?: boolean;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
  children: (props: InlineInputProps<T>) => ReactNode;
  layout?: 'inline' | 'stacked';
}

const getCopyableValue = (value: unknown) => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return undefined;
};

const getInlineInputUtilityItems = ({
  value,
  disableCopy,
  isCopied,
  t,
  copyToClipboard,
}: {
  value: unknown;
  disableCopy: boolean;
  isCopied: boolean;
  t: ReturnType<typeof useI18n>['t'];
  copyToClipboard: (value?: string) => Promise<boolean>;
}) =>
  [
    getCopyableValue(value) && !disableCopy
      ? {
          content: isCopied
            ? t('shared.inline.copied')
            : t('shared.inline.copy'),
          icon: isCopied ? (
            <PiCheckBold className="h-4 w-4 text-success-500" />
          ) : (
            <PiCopyLight className="h-4 w-4" />
          ),
          onClick: () => copyToClipboard(getCopyableValue(value)),
        }
      : null,
    value && typeof value === 'string' && isProbablyUrl(value)
      ? {
          content: t('shared.inline.openLink'),
          icon: <PiArrowSquareOut className="h-4 w-4" />,
          onClick: () =>
            window.open(ensureUrlWithProtocol(value), '_blank', 'noopener'),
        }
      : null,
  ].filter(Boolean) as DropdownItem[];

const InlineInputUtilityActions = ({
  utilityItems,
  isStacked,
  label,
  size,
  iconSize,
  isCopied,
  value,
  disableCopy,
  disabled,
  t,
  copyToClipboard,
}: {
  utilityItems: DropdownItem[];
  isStacked: boolean;
  label: string;
  size: ComponentSize;
  iconSize: string;
  isCopied: boolean;
  value: unknown;
  disableCopy: boolean;
  disabled: boolean;
  t: ReturnType<typeof useI18n>['t'];
  copyToClipboard: (value?: string) => Promise<boolean>;
}) => {
  if (utilityItems.length === 0) return null;

  return (
    <div
      className={
        isStacked
          ? 'order-2 flex shrink-0 items-center gap-1'
          : 'opacity-0 transition-opacity duration-200 group-hover:opacity-100'
      }
    >
      {isStacked ? (
        <Dropdown
          items={utilityItems}
          placement="bottom-end"
          mobileTitle={label}
          handler={
            <Button
              variant="neutral"
              appearance="ghost"
              size={size}
              title={t('shared.inline.actions')}
              isIconOnly
              disabled={disabled}
            >
              <PiDotsThreeVertical className={iconSize} />
            </Button>
          }
        />
      ) : (
        <>
          {getCopyableValue(value) && !disableCopy && (
            <Button
              variant="neutral"
              appearance="ghost"
              size={size}
              onClick={() => {
                void copyToClipboard(getCopyableValue(value));
              }}
              title={
                isCopied ? t('shared.inline.copied') : t('shared.inline.copy')
              }
              isIconOnly
              disabled={disabled}
            >
              {isCopied ? (
                <PiCheckBold className={`${iconSize} text-success-500`} />
              ) : (
                <PiCopyLight className={iconSize} />
              )}
            </Button>
          )}
          {value && typeof value === 'string' && isProbablyUrl(value) && (
            <Tooltip
              content={t('shared.inline.openLink')}
              placement="top"
              size="sm"
            >
              <Button
                variant="neutral"
                appearance="ghost"
                size={size}
                onClick={() =>
                  window.open(
                    ensureUrlWithProtocol(value),
                    '_blank',
                    'noopener'
                  )
                }
                title={t('shared.inline.openLink')}
                isIconOnly
                disabled={disabled}
              >
                <PiArrowSquareOut className={iconSize} />
              </Button>
            </Tooltip>
          )}
        </>
      )}
    </div>
  );
};

/**
 * A wrapper component for inline-editable fields that change immediately
 * without an explicit edit/save mode (like checkboxes, selects, etc.)
 */
export const InlineInputWrapper = <T,>({
  label,
  value,
  onSave,
  loading = false,
  size = 'sm',
  variant = 'primary',
  disabled = false,
  disableCopy = false,
  onError,
  onSuccess,
  children,
  hideLabel = false,
  layout = 'inline',
}: InlineInputWrapperProps<T>) => {
  const { t } = useI18n();
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  const handleChange = async (newValue: T) => {
    try {
      await onSave(newValue);
      onSuccess?.();
    } catch (error) {
      onError?.(error as Error);
    }
  };

  const iconSize = getInlineIconSize(size);
  const isStacked = layout === 'stacked';
  const stackedFieldValueReadableClassName = stackedFieldValueClassName
    .replace(/!/g, '')
    .trim();
  const utilityItems = getInlineInputUtilityItems({
    value,
    disableCopy,
    isCopied,
    t,
    copyToClipboard,
  });

  return (
    <div className="group relative">
      <div
        className={
          isStacked
            ? 'flex w-full flex-col gap-0.5'
            : 'flex items-center justify-between w-full px-2'
        }
      >
        {!hideLabel && (
          <Label
            size="xs"
            variant={isStacked ? 'secondary' : variant}
            layout={isStacked ? 'stacked' : 'inline'}
            className={
              isStacked
                ? 'break-words'
                : 'flex-shrink-0 font-light max-w-[40%] break-words'
            }
          >
            {isStacked ? label : `${label}:`}
          </Label>
        )}
        <div
          className={
            isStacked
              ? 'flex w-full items-center gap-2'
              : `flex-1 flex items-center justify-end ${hideLabel ? 'min-w-0' : 'min-w-[40%]'}`
          }
        >
          <div
            className={
              isStacked
                ? `stacked-field-control flex w-full items-center gap-2 ${stackedFieldValueReadableClassName}`
                : 'flex items-center gap-2'
            }
          >
            <div className="flex-grow min-w-0 order-1">
              {children({
                value: value,
                onChange: (nextValue) => {
                  void handleChange(nextValue);
                },
                disabled: loading || disabled,
                loading: loading,
              })}
            </div>
            <InlineInputUtilityActions
              utilityItems={utilityItems}
              isStacked={isStacked}
              label={label}
              size={size}
              iconSize={iconSize}
              isCopied={isCopied}
              value={value}
              disableCopy={disableCopy}
              disabled={disabled}
              t={t}
              copyToClipboard={copyToClipboard}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
